import React from 'react';
import { Game } from 'app-types';
import { TileItem } from 'store-types';
import { useHistory } from 'react-router-dom';
import { MemoryHistory } from 'history';

import LiveTile from 'components/common/tile/LiveTile';
import EventTile from 'components/common/tile/EventTile';
import DvrTile from 'components/common/tile/DvrTile';
import VodTile from 'components/common/tile/VodTile';
import PromotionTile from 'components/common/tile/PromotionTile';

import { getSizeByLayout } from 'support/mediaTileHelper';

import { useCurrentVideoIds } from 'hooks/video';
import { isUpcoming } from 'support/videoHelper';
import { getImageUrl } from 'support/imageHelper';
import { getGamebyVideo } from 'support/scoringHelper';
import { KitchenSinkRowItem } from 'store-types';

interface HomeTileProps {
  row: KitchenSinkRowItem;
  item: TileItem;
  displayScoring?: boolean;
  games?: Game[] | null;
  onClick?: (props: { rowId: string; video: TileItem; rowSlug?: string }) => void;
  className?: string | null;
  domRef?: React.Ref<HTMLSpanElement> | null;
  focusIdPrefix?: string | null;
}

const HomeTile: React.FC<HomeTileProps> = ({
  row,
  item,
  displayScoring = false,
  games = null,
  onClick = () => null,
  className = null,
  domRef = null,
  focusIdPrefix = '',
}) => {
  const history = useHistory() as MemoryHistory;

  const onPromoClick = () => {
    const tag = item?.tagKey;
    const { tagType = '' } = item ?? {};
    let page = null;
    if (tagType.toLowerCase().includes('league')) {
      page = 'league';
    } else if (tagType.toLowerCase().includes('sport')) {
      page = 'sport';
    } else if (tagType.toLowerCase().includes('show')) {
      page = 'show';
    }

    if (tag && page) {
      history.push({
        pathname: `/${page}/${tag}`,
      });
    }
  };

  const game = displayScoring ? getGamebyVideo(item, games) : null;

  const [currentVideoId] = useCurrentVideoIds();
  if (item.type === 'PROMO') {
    const src = item.images
      ? getImageUrl(item.images.baseImage16X5, 1360)
      : null;
    return (
      <span className="flex-shrink-0" ref={domRef} key={item.id}>
        <PromotionTile
        ttsText={item.title}
        src={src ?? undefined}
        className={className ?? undefined}
        focusId={`row-${row?.slug}-item-${item.id}`}
        onClick={onPromoClick}
      />
    </span>
    );
  }
  const {
    about: { title, shortTitle, duration, prefix, publishedOn },
    schedule,
    network,
    id,
    event,
  } = item;
  const videoStartDate = schedule?.videoStartDate;
  const status = event?.status;

  const tileProps = {
    size: getSizeByLayout(row.layoutType),
    focusId: `${focusIdPrefix}row-${row.slug}-item-${id}`,
    className: className ?? undefined,
    src: item.images?.baseImage16X9 ?? undefined,
    autoSizeSrc: true,
    alt: title,
    id: `${focusIdPrefix}row-${row.slug}-item-${id}`,
    isNowPlaying: id === currentVideoId,
    onClick: () => {
      onClick?.({ rowId: row.id, video: item, rowSlug: row.slug });
    },
    displayScoring,
    game,
  };

  switch (row.type) {
    case 'event': {
      const isLive = !isUpcoming(item);

      const eventProps = {
        title: shortTitle,
        prefix,
        brand: network,
      };
      return (
        <span className="flex-shrink-0" ref={domRef} key={item.id}>
          {isLive ? (
            <LiveTile
              {...eventProps}
              {...tileProps}
              authTypes={item.authTypes}
              startDate={(videoStartDate as number) * 1000}
              status={status}
            />
          ) : (
            <EventTile
              {...eventProps}
              {...tileProps}
              authTypes={item.authTypes}
              startDate={(videoStartDate as number) * 1000}
            />
          )}
        </span>
      );
    }
    case 'dvr': {
      const dvrProps = {
        title: shortTitle,
        timeSeconds: publishedOn,
      };
      return (
        <span className="flex-shrink-0" ref={domRef} key={item.id}>
          <DvrTile {...dvrProps} {...tileProps} />
        </span>
      );
    }
    default: {
      const vodProps = {
        title: shortTitle,
        duration,
        brand: network,
        authTypes: item.authTypes,
      };
      return (
        <span className="flex-shrink-0" ref={domRef} key={item.id}>
          <VodTile {...vodProps} {...tileProps} />
        </span>
      );
    }
  }
};

export default React.memo(
  React.forwardRef<HTMLSpanElement, Omit<HomeTileProps, 'domRef'>>((props, ref) => <HomeTile {...props} domRef={ref} />),
);
